import {
  Button,
  Card,
  Input,
  Select,
  Table,
  TablePagination,
} from "@result/rui/components";
import { useForm, useNotifications } from "@result/rui/hooks";
import { ApiFilter, ApiPage } from "@result/rui/utils/api";
import { toHumanReadableDate } from "@result/rui/utils/date";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import * as yup from "yup";

import DateInput from "#/components/DateInput";
import PageHeader from "#/components/PageHeader.jsx";
import SelectLookupTableItem from "#/components/SelectLookupTableItem";
import DataLayerSearchSelect from "#/components/data-layer/SearchSelect";
import * as DLD from "#/def/data-layer";
import { PLEMENSKA_VREDNOST_KODA } from "#/def/data-layer";
import { SifSpecies } from "#/def/lookup-tables.enums.gen";
import * as LTD from "#/def/lookup-tables.ident.gen";
import { useDataLayerQuery } from "#/hooks/useDataLayer";
import { useLookupTableList } from "#/hooks/useLookupTables";
import yesNoOptions from "#/model/YesNoOptions";
import { filterLastnostiPvBySpecies } from "#/pages/breeding-values/BreedingValueUtils";
import BreedingValuesPresets from "#/pages/breeding-values/BreedingValuesPresets";
import BreedingValuesTipPvCheckbox from "#/pages/breeding-values/BreedingValuesTipPvCheckbox";
import KodaIzracunSelect from "#/pages/breeding-values/KodaIzracunSelect";
import { useBreedingValuesBreedQuery } from "#/pages/breeding-values/useBreedingValuesBreedQuery";
import { useBreedingValuesComparisonRankQuery } from "#/pages/breeding-values/useBreedingValuesComparisonRankQuery";
import { useBreedingValuesKodaIzracunQuery } from "#/pages/breeding-values/useBreedingValuesKodaIzracunQuery";
import * as species from "#/utils/SpeciesScope";

const defaultLastnost = {
  KOZA: "OVCA_KODPV_168",
  OVCA: "OVCA_KODPV_168",
  PRASIC: "SPV",
  KONJ: "KOPITAR_KODPV_1",
  GOVEDO: "GOVEDO_KODPV_168",
};

const BreedingValuesComparisonRankPage = () => {
  const { t } = useTranslation();
  const { addErrorNotification, addWarningNotification } = useNotifications();
  const [lastFetchValues, setLastFetchValues] = useState({});
  const [pedigreeLoadingPlemenskaZivalId, setPedigreeLoadingPlemenskaZivalId] =
    useState(null);

  const validationSchema = {
    lastnost: yup.string().required(),
    kodeIzracun: yup.array().min(1).required(),
  };

  const breedsQuery = useBreedingValuesBreedQuery();
  const eliminationOptions = useMemo(() => yesNoOptions(t), [t]);

  useEffect(() => {
    breedsQuery.query({});
  }, []);

  const filteredBreeds = useMemo(() => {
    return (breedsQuery.result?.list ?? []).filter((breed) =>
      species.has(breed.species)
    );
  }, [breedsQuery.result]);

  const fetchComparisonRank = (values, { page = 0 }) => {
    setLastFetchValues(values);
    comparisonRankQuery
      .query({
        body: {
          ...values,
          page: page,
          pageSize: 100,
        },
      })
      .catch((e) => {
        console.log("Error fetching breeding values", e);
        addErrorNotification(t("breedingValues.errorFetching"));
      });
  };

  const metodeIzracuna = useLookupTableList(LTD.SifPlemVredMetodaIzracuna);
  const supportedSpecies = species.getTranslated();

  const [breedingValueCodes, setBreedingValueCodes] = useState([]);
  const kodaIzracunQuery = useBreedingValuesKodaIzracunQuery();
  const [isInitialFetchTriggered, setIsInitialFetchTriggered] = useState(false);
  const [initialValues, setInitialValues] = useState({});
  const zivalIdentifikatorQuery = useDataLayerQuery(DLD.ZIVAL_IDENTIFIKATOR);

  const navigate = useNavigate();

  const distinctSklopi = useMemo(() => {
    return Array.from(new Set(breedingValueCodes.map((bv) => bv.sklop))).filter(
      Boolean
    );
  }, [breedingValueCodes]);

  const breedingValueCodeQuery = useDataLayerQuery({
    ident: PLEMENSKA_VREDNOST_KODA,
  });

  useEffect(() => {
    if (kodaIzracunQuery.result?.list == null) {
      kodaIzracunQuery.query();
    }

    getField("lastnost").setValue(defaultLastnost[supportedSpecies?.[0]]);
  }, [supportedSpecies?.[0]]);

  useEffect(() => {
    if (
      kodaIzracunQuery.result?.list == null ||
      kodaIzracunQuery.result.list.length === 0
    ) {
      return;
    }
    getField("kodeIzracun").setValue([kodaIzracunQuery.result.list[0]]);
  }, [kodaIzracunQuery.result]);

  useEffect(() => {
    breedingValueCodeQuery
      .query({
        page: new ApiPage().page(0).size(1000),
        filter: filterLastnostiPvBySpecies(),
      })
      .then((result) => {
        setBreedingValueCodes(result.content ?? []);
      });
  }, []);

  const { getField, submit, values, setValues } = useForm({
    initialValues: {
      kodeIzracun: [],
    },
    schema: validationSchema,
    onSubmit: fetchComparisonRank,
  });

  useEffect(() => {
    if (
      !isInitialFetchTriggered &&
      values.kodeIzracun != null &&
      values.kodeIzracun.length > 0 &&
      values.lastnost != null
    ) {
      setIsInitialFetchTriggered(true);
      setInitialValues(values);
      submit(values);
    }
  }, [values.kodeIzracun?.length, values.lastnost]);

  const comparisonRankQuery = useBreedingValuesComparisonRankQuery();

  const showPedigree = (identifikatorZivali) => {
    setPedigreeLoadingPlemenskaZivalId(identifikatorZivali);
    zivalIdentifikatorQuery
      .query({
        filter: new ApiFilter()
          .equal("vrednost", identifikatorZivali)
          .in("tip", ["USESNA_STEVILKA", "INTERBUL"]),
      })
      .then((result) => {
        if (result?.content?.length === 0) {
          addWarningNotification(
            t("breedingValues.animalNotFoundWithIdentifier", {
              identifier: identifikatorZivali,
            })
          );
          return;
        }
        const animalId = result.content[0].zivalId;
        if (animalId) {
          navigate(`/animal/${animalId}/pedigree`);
        }
      })
      .catch((_) => {
        addErrorNotification(t("breedingValues.errorFetchingAnimalData"));
      })
      .finally(() => {
        setPedigreeLoadingPlemenskaZivalId(null);
      });
  };

  const breedingValueTableColumns = useMemo(() => {
    return (lastFetchValues.kodeIzracun ?? [])
      .sort()
      .reverse()
      .flatMap((kod, index) => {
        const color =
          index % 2 === 0 ? "pokk-sky-blue" : "pokk-vivid-grass-green";
        return [
          {
            id: `plemenskeVrednosti.${kod}.kodobr`,
            name: "Izr",
            cellComponent: (rang) => {
              const breedingValue = rang.plemenskeVrednosti?.[kod];
              return (
                <div className={`is-${color} is-darker`}>
                  {breedingValue ? kod : "-"}
                </div>
              );
            },
          },
          {
            id: `plemenskeVrednosti.${kod}.n`,
            name: "N",
            cellComponent: (rang) => {
              const breedingValue = rang.plemenskeVrednosti?.[kod];
              return (
                <div className={`is-${color} is-darker`}>
                  {breedingValue?.steviloUporabljenihPodatkov ?? "-"}
                </div>
              );
            },
          },
          {
            id: `plemenskeVrednosti.${kod}.pv12`,
            name: "PV12",
            cellComponent: (rang) => {
              const breedingValue = rang.plemenskeVrednosti?.[kod];
              return (
                <div className={`is-${color} is-darker`}>
                  {breedingValue ? breedingValue.standardiziranaPv : "-"}
                </div>
              );
            },
          },
          {
            id: `plemenskeVrednosti.${kod}.rang`,
            name: "Rang",
            cellComponent: (rang) => {
              const breedingValue = rang.plemenskeVrednosti?.[kod];
              return (
                <div className={`is-${color} is-darker`}>
                  {breedingValue ? breedingValue.rangAbsolutno : "-"}
                </div>
              );
            },
          },
          {
            id: `plemenskeVrednosti.${kod}.percentRang`,
            name: "%Rang",
            cellComponent: (rang) => {
              const breedingValue = rang.plemenskeVrednosti?.[kod];
              return (
                <div className={`is-${color} is-darker`}>
                  {breedingValue ? breedingValue.rangProcentno : "-"}
                </div>
              );
            },
          },
        ];
      });
  }, [lastFetchValues?.kodeIzracun?.length]);

  return (
    <form className="breeding-values-page" onSubmit={submit}>
      <PageHeader
        title={t("breedingValues.comparisonRank")}
        subtitle={t("breedingValues.comparisonRank.description")}
        backURL=".."
      />

      <BreedingValuesPresets
        storageKey="BREEDING_VALUES_COMPARISON_RANK_PRESETS"
        values={values}
        onLoad={(newValues) => {
          setValues(newValues);
          fetchComparisonRank(newValues, { page: 0 });
        }}
      />
      <Card>
        <div className="fixed-grid has-1-cols-mobile has-2-cols">
          <div className="grid">
            <Select
              options={distinctSklopi}
              label={t("breedingValues.section")}
              labelType="floating"
              formField={getField("sklop")}
            />
            <DataLayerSearchSelect
              ident={DLD.PLEMENSKA_VREDNOST_KODA}
              queryFields={["sifra", "naziv.sl"]}
              optionId="sifra"
              labelType="floating"
              optionLabel={(koda) =>
                [koda.naziv?.sl, koda.sifra]
                  .filter(Boolean)
                  .join(" - ")
                  .toUpperCase()
              }
              optionValue="sifra"
              selectionMode="single"
              filter={() => {
                const filter = filterLastnostiPvBySpecies();
                if (values.sklop != null) {
                  filter.equal("sklop", values.sklop);
                }
                return filter;
              }}
              label={t("breedingValues.property")}
              formField={getField("lastnost")}
            />
            <KodaIzracunSelect
              formField={getField("kodeIzracun")}
              labelType="floating"
            />
            <SelectLookupTableItem
              labelType="floating"
              ident={LTD.SifSpol}
              formField={getField("spolSifra")}
            />
            <Input
              type="number"
              formField={getField("lokacija")}
              label={t("breedingValues.locationKmgMid")}
              labelType="floating"
            />
            <SelectLookupTableItem
              ident={LTD.SifPlemVredKodaPopulacije}
              label={t("breedingValues.populationCode")}
              optionLabel={(kodaPopulacije) =>
                kodaPopulacije.sifra + " - " + kodaPopulacije.naziv?.sl
              }
              labelType="floating"
              formField={getField("populacija")}
            />
            <Select
              options={filteredBreeds}
              label={t("animal.breed")}
              formField={getField("pasma")}
              optionId="sifra"
              optionValue="sifra"
              labelType="floating"
              optionLabel={(breed) =>
                [breed.sifra, breed.pasma?.naziv].filter(Boolean).join(" - ")
              }
            />
            <Select
              formField={getField("metodaIzracun")}
              label={t("breedingValues.calculationMethods")}
              options={metodeIzracuna}
              optionId="sifra"
              optionValue="sifra"
              labelType="floating"
              optionLabel={(metoda) =>
                [metoda.sifra, metoda.naziv?.sl].filter(Boolean).join(" - ")
              }
            />
          </div>
        </div>
        <div className="fixed-grid has-2-cols-mobile has-4-cols-tablet">
          <div className="grid">
            <Input
              label={
                supportedSpecies?.[0] === SifSpecies.KONJ
                  ? t("animal.lifeNumber")
                  : t("animalId")
              }
              labelType="floating"
              formField={getField("zivalId")}
            />

            <DateInput
              labelType="floating"
              label={t("breedingValues.birthFrom")}
              formField={getField("rojstvoOd")}
            />
            <DateInput
              labelType="floating"
              label={t("breedingValues.birthTo")}
              formField={getField("rojstvoDo")}
            />
            <Select
              labelType="floating"
              options={eliminationOptions}
              label={t("breedingValues.elimination")}
              optionId="id"
              optionLabel="label"
              optionValue="id"
              formField={getField("izlocitev")}
            />
          </div>
        </div>
        <BreedingValuesTipPvCheckbox
          selectionMode="SINGLE"
          formField={getField("tipPv")}
        />
        <div className="is-flex is-justify-content-space-between mt-4">
          <Button
            label={t("common.search")}
            isSubmit={true}
            isLoading={comparisonRankQuery.isLoading}
            color="primary"
          />
          <Button
            label={t("common.reset")}
            onClick={() => {
              setValues(initialValues);
              submit(initialValues);
            }}
          />
        </div>
      </Card>

      <Card>
        <Table
          data={comparisonRankQuery?.result?.content ?? []}
          columns={[
            {
              id: "plemenskaZival.identifikator",
              name: supportedSpecies?.[0] === SifSpecies.KONJ ? "ŽŠ" : "ID",
              cellComponent: (rang) =>
                rang?.plemenskaZival?.eksterniEnolicniIdentifikator ?? "-",
            },
            {
              id: "pedigree",
              isSortable: false,
              cellComponent: (rank) => {
                const isLoading =
                  pedigreeLoadingPlemenskaZivalId ===
                  rank.plemenskaZival?.eksterniEnolicniIdentifikator;

                if (supportedSpecies?.[0] === "CATTLE") {
                  return "";
                }

                if (isLoading) {
                  return <span className="loader"></span>;
                }

                return (
                  <span
                    className="has-radius-small has-background-light py-1 px-2"
                    data-tooltip={t("breedingValues.showPedigree")}
                    onClick={() => {
                      showPedigree(
                        rank.plemenskaZival?.eksterniEnolicniIdentifikator
                      );
                    }}>
                    R
                  </span>
                );
              },
            },
            {
              id: "pedigreeNumber",
              name: t("breedingValues.columns.pedigreeNum"),
              cellComponent: (rang) =>
                rang.plemenskaZival?.rodovniska_stevilka ?? "-",
            },
            {
              id: "pasma",
              name: t("breedingValues.columns.breed"),
              cellComponent: (rang) => rang?.plemenskaZival?.pasmaSifra,
            },
            {
              id: "location",
              name: t("breedingValues.columns.location"),
              cellComponent: (rank) => {
                return rank.plemenskaZival?.trenutna_lokacija_kmg_mid ?? "-";
              },
            },
            {
              id: "birthDate",
              name: t("breedingValues.columns.birthDate"),
              cellComponent: (rang) =>
                rang?.plemenskaZival?.datumRojstva
                  ? toHumanReadableDate(rang?.plemenskaZival?.datumRojstva)
                  : "-",
            },
            {
              id: "elimination",
              name: t("breedingValues.elimination"),
              cellComponent: (rang) =>
                rang?.plemenskaZival?.izlocena == null
                  ? "-"
                  : rang?.plemenskaZival?.izlocena
                    ? "Da"
                    : "Ne",
            },
            ...breedingValueTableColumns,
          ]}
        />
        <TablePagination
          onPage={(page) => {
            fetchComparisonRank(values, { page });
          }}
          previousPageLabel={t("pagination.previousPage")}
          nextPageLabel={t("pagination.nextPage")}
          page={comparisonRankQuery.result}
        />
      </Card>
    </form>
  );
};

export default BreedingValuesComparisonRankPage;
